import { stringify } from 'csv-stringify/sync';
import type { AIAssistantExportFileInputData } from 'balus-common';
import type { Cell, SheetData, Sheet } from 'write-excel-file/browser';

/**
 * exportFile クライアントツールのファイル生成。モデルが渡した構造化データ（シート×行×セル）から
 * xlsx / csv / tsv、またはプレーンテキストから md / txt の Blob を生成する。
 * xlsx の生成エンジンは動的 import で遅延ロードし、初期バンドルに載せない。
 * 生成した Blob は自動ダウンロードせず、呼び出し側（AIAssistantChat）がストアに保持して
 * チャット上のカード（ExportFileCard）のクリック時にのみダウンロードする。
 */

/**
 * シリアライズ後の合計文字数の上限。ツール引数（rows 全体）は assistant メッセージとして
 * RTDB 履歴（payload ≤1,000,000字）に保存されるため、セル数上限だけでは不十分。ここでも打ち止める（03計画 R14）。
 */
export const EXPORT_FILE_MAX_SERIALIZED_CHARS = 300_000;

const UTF8_BOM = '\uFEFF';

const MIME_BY_FORMAT = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
  tsv: 'text/tab-separated-values',
  md: 'text/markdown',
  txt: 'text/plain',
} as const;

// ヘッダ行の最小スタイル（v1）。bold + 薄いグレー背景のみ。
const HEADER_STYLE = { fontWeight: 'bold', backgroundColor: '#F2F2F2' } as const;

// 表形式（xlsx/csv/tsv）の1シート分の型。sheets は v.check により表形式のときのみ存在が保証される。
type ExportSheet = NonNullable<AIAssistantExportFileInputData['sheets']>[number];

// content は表形式でも optional なキーとして存在しうる（値が undefined でも 'in' 判定は真になる）ため、
// プロパティの有無ではなく format の値で表形式/テキストを判定する。
function isTextFormat(format: AIAssistantExportFileInputData['format']): boolean {
  return format === 'md' || format === 'txt';
}

type ExportChatFileResult = { blob: Blob; downloadFilename: string };

/** 生成データが大きすぎる等、モデルに分割出力を促したいエラー。 */
export class ExportChatFileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ExportChatFileError';
  }
}

export async function generateExportChatFile(input: AIAssistantExportFileInputData): Promise<ExportChatFileResult> {
  const serializedLength = JSON.stringify(input).length;
  if (serializedLength > EXPORT_FILE_MAX_SERIALIZED_CHARS) {
    // 表形式は行数・列数、テキスト系は content を減らすよう、format に応じて案内文を出し分ける。
    const advice = isTextFormat(input.format)
      ? 'content を減らすか、ファイルを分割して出力してください。'
      : '行数・列数を減らすか、シートやファイルを分割して出力してください。';
    throw new ExportChatFileError(
      `出力データが大きすぎます（${serializedLength.toLocaleString()}字 > ${EXPORT_FILE_MAX_SERIALIZED_CHARS.toLocaleString()}字）。${advice}`
    );
  }

  const downloadFilename = buildDownloadFilename(input.filename, input.format);
  const blob = await buildExportBlob(input);
  return { blob, downloadFilename };
}

async function buildExportBlob(input: AIAssistantExportFileInputData): Promise<Blob> {
  // format の値で分岐する（sheets/content の排他性はスキーマの v.check で保証済み）。
  if (isTextFormat(input.format)) return buildTextBlob(input.format, input.content!);
  const sheets = input.sheets ?? [];
  if (input.format === 'xlsx') return buildXlsxBlob(sheets);
  return buildDelimitedBlob(sheets, input.format as 'csv' | 'tsv');
}

// 拡張子は format から付与する。モデルが誤って拡張子を含めた場合は付け替える。
function buildDownloadFilename(filename: string, format: AIAssistantExportFileInputData['format']): string {
  const base = filename.replace(/\.(xlsx|csv|tsv|md|txt)$/iu, '');
  return `${base}.${format}`;
}

function buildTextBlob(format: AIAssistantExportFileInputData['format'], content: string): Blob {
  // md/txt は csv/tsv と違い Excel 互換の配慮が不要なため BOM は付けない。
  return new Blob([content], { type: MIME_BY_FORMAT[format] });
}

function buildDelimitedBlob(sheets: ExportSheet[], format: 'csv' | 'tsv'): Blob {
  // csv/tsv は1シートのみ（スキーマで保証済み）。
  const sheet = sheets[0];
  const records = sheet.rows.map((row) => row.map(cellToText));
  const body = stringify(records, { delimiter: format === 'tsv' ? '\t' : ',' });
  // Excel の直接開きで文字化けしないよう UTF-8 BOM を付与する。
  return new Blob([UTF8_BOM + body], { type: MIME_BY_FORMAT[format] });
}

function cellToText(cell: string | number | boolean | null): string {
  // 空セルは空文字。数式インジェクション対策の危険プレフィックス（= + - @）のエスケープはしない
  // （データ改変になるため）。区切り・改行・引用符は csv-stringify がクォートで処理する（03計画 §5-2）。
  return cell === null ? '' : String(cell);
}

async function buildXlsxBlob(inputSheets: ExportSheet[]): Promise<Blob> {
  const { default: writeXlsxFile } = await import('write-excel-file/browser');
  const usedNames = new Set<string>();
  const sheets: Sheet<Blob>[] = inputSheets.map((sheet, index) => ({
    data: toWriteExcelRows(sheet),
    sheet: uniqueSheetName(sanitizeSheetName(sheet.name, index), usedNames),
    columns: sheet.columnWidths?.map((width) => ({ width })),
  }));
  return writeXlsxFile(sheets).toBlob();
}

function toWriteExcelRows(sheet: ExportSheet): SheetData {
  const headerRows = sheet.headerRowCount ?? 0;
  return sheet.rows.map((row, rowIndex) => row.map((cell) => toWriteExcelCell(cell, rowIndex < headerRows)));
}

function toWriteExcelCell(cell: string | number | boolean | null, isHeader: boolean): Cell {
  // 文字列は常に type: String で書き込み、= や + で始まる値も数式として解釈させない（数式インジェクション対策）。
  if (cell === null) {
    // 空セル。ヘッダ行では値を持たないままスタイルだけ適用する。
    return isHeader ? { ...HEADER_STYLE } : null;
  }
  const style = isHeader ? HEADER_STYLE : undefined;
  if (typeof cell === 'number') return { value: cell, type: Number, ...style };
  if (typeof cell === 'boolean') return { value: cell, type: Boolean, ...style };
  return { value: cell, type: String, ...style };
}

// Excel のシート名は []:*?/\ を含められず、31文字まで。未指定・空になる場合は連番で補う。
function sanitizeSheetName(name: string | undefined, index: number): string {
  const cleaned = (name ?? '')
    .replace(/[[\]:*?/\\]/gu, '_')
    .trim()
    .slice(0, 31);
  return cleaned.length > 0 ? cleaned : `Sheet${index + 1}`;
}

// 同名シートは Excel が拒否するため、重複時は末尾に連番を付ける。
function uniqueSheetName(name: string, used: Set<string>): string {
  if (!used.has(name)) {
    used.add(name);
    return name;
  }
  for (let i = 2; ; i++) {
    const suffix = `_${i}`;
    const candidate = `${name.slice(0, 31 - suffix.length)}${suffix}`;
    if (!used.has(candidate)) {
      used.add(candidate);
      return candidate;
    }
  }
}
